import { Injectable } from '@angular/core';
import { flatten, isArray } from 'lodash';
import { GlobalConstants } from '../constant/constant';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction } from '../models/db/db-transaction';
import { FilterService } from './filter.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class RepositoryService {
  constructor(private filterService: FilterService, private storageService: StorageService) {}

  async create(item: any, storeName: string, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readwrite'));
    const recordId = await this.storageService.createData(item, storeName, transaction);
    return recordId;
  }

  async insertBulkData(item: any, storeName: string): Promise<number> {
    return await this.storageService.insertBulkData(item, storeName);
  }

  async update(item: any, storeName: string, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readwrite'));
    const recordId = await this.storageService.updateData(item, storeName, transaction);
    return recordId;
  }

  async delete(localId: number, storeName: string, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readwrite'));
    await this.storageService.deleteData(localId, storeName, transaction);
  }

  async deleteBulkData(item: any, storeName: string): Promise<number> {
    return await this.storageService.deleteBulkData(item, storeName);
  }

  async deleteAll(storeName: string): Promise<void> {
    return await this.storageService.deleteAll(storeName);
  }

  async getByLocalId<T>(storeName: string, localId: number, T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<any> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));

    const res = await this.storageService.getOne(storeName, localId, transaction);

    if (res) {
      if (T) {
        return new T(res);
      } else {
        return res;
      }
    } else {
      return null;
    }
  }

  async getCount(storeName: string): Promise<number> {
    return await this.storageService.getCount(storeName);
  }

  async getAllPartialFields<T, K>(storeName: string, K: new (record: any) => K, filter?: any, T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<any[]> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));

    const res = await this.storageService.getAllPartialFields(storeName, K, transaction);

    let filteredResult = res;
    if (filter) {
      filteredResult = this.filterService.getFilteredData(res, filter);
    }
    if (T) {
      return filteredResult.map(record => new T(record));
    } else {
      return filteredResult;
    }
  }

  async getAllPartialFieldsByIndex<T, K>(
    storeName: string,
    indexName: string,
    indexKey: DbKeyType | DbKeyType[],
    K: new (record: any) => K,
    T?: new (record: any) => T,
    existingTransaction?: DbTransaction
  ): Promise<any[]> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));
    let result;
    if (isArray(indexKey)) {
      const promises = (indexKey as DbKeyType[]).map(key => this.storageService.getAllPartialFieldsByIndex(storeName, indexName, key, K, transaction));
      result = await Promise.all(promises);
      result = flatten(result);
    } else {
      result = await this.storageService.getAllPartialFieldsByIndex(storeName, indexName, indexKey, K, transaction);
    }
    if (T) {
      return result.map(record => new T(record));
    } else {
      return result;
    }
  }

  async getAll<T>(storeName: string, filter?: any, T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<any[]> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));

    const res = await this.storageService.getAll(storeName, transaction);

    let filteredResult = res;
    if (filter) {
      filteredResult = this.filterService.getFilteredData(res, filter);
    }

    if (T) {
      return filteredResult.map(record => new T(record));
    } else {
      return filteredResult;
    }
  }

  async getOnePartialFieldsByIndex<T, K>( storeName: string, indexName: string, indexKey: DbKeyType, K: new (record: any) => K, T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<T> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));

    let res = await this.storageService.getOnePartialFieldsByIndex(storeName, indexName, indexKey, K, transaction);
    if (!res) {
      return null;
    }

    if (T) {
      return new T(res);
    } else {
      return res;
    }
  }


  async getOneByIndex<T>(storeName: string, indexName: string, indexKey: DbKeyType, T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<T> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));

    try {
      let res = await this.storageService.getOneByIndex(storeName, indexName, indexKey, transaction);
      if (!res) {
        return null;
      }

      if (T) {
        return new T(res);
      } else {
        return res;
      }
    } catch {
      return null;
    }
  }

  async getAllByIndex<T>(storeName: string, indexName: string, indexKey: DbKeyType | DbKeyType[], T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<T[]> {
    const transaction = existingTransaction || (await this.startTransaction([storeName], 'readonly'));
    let result;

    if (isArray(indexKey)) {
      const promises = (indexKey as DbKeyType[]).map(key => this.storageService.getAllByIndex(storeName, indexName, key, transaction));
      result = await Promise.all(promises);
      result = flatten(result);
    } else {
      result = await this.storageService.getAllByIndex(storeName, indexName, indexKey, transaction);
    }

    if (T) {
      return result.map(record => new T(record));
    } else {
      return result;
    }
  }

  async getByOnlineId<T>(storeName: string, id: DbKeyType, T?: new (record: any) => T, existingTransaction?: DbTransaction): Promise<any> {
    return await this.getOneByIndex(storeName, GlobalConstants.KEY_NAMES.Id, id, T, existingTransaction);
  }

  startTransaction(storeNames: string[], mode: 'readwrite' | 'readonly'): Promise<DbTransaction> {
    return this.storageService.startTransaction(storeNames, mode);
  }

  async commitTransaction(transaction: DbTransaction): Promise<void> {
    await transaction.commit();
  }

  async rollbackTransaction(transaction: DbTransaction): Promise<void> {
    await transaction.rollback();
  }
}
